import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/HealthDashboard.css';
import { API_BASE_URL } from '../services/ApiService';
import writeXlsxFile from 'write-excel-file/browser';
import UserMenu from '../components/UserMenu';

/**
 * Health Dashboard - System monitoring and metrics
 * Shows real-time resource usage, uptime, and system health
 * Protected: super_admin only
 */
const HealthDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeWindow, setActiveWindow] = useState(15);
  const [historyData, setHistoryData] = useState([]);
  const [dateStart, setDateStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateEnd, setDateEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const maxCountEver = React.useRef(1);
  const chartRef = React.useRef(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const fetchMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health?activeWindow=${activeWindow}`);
      if (!response.ok) throw new Error('Failed to fetch metrics');
      const data = await response.json();
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch history data from the dedicated date-range endpoint
  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health/user-history-range?startDate=${dateStart}&endDate=${dateEnd}`);
      if (!response.ok) return;
      const result = await response.json();
      setHistoryData(result.data ?? []);
    } catch {
      // Silently fail — graph just won't update
    }
  }, [dateStart, dateEnd]);

  useEffect(() => {
    fetchMetrics();
    fetchHistory();

    if (autoRefresh) {
      const interval = setInterval(() => { fetchMetrics(); fetchHistory(); }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, activeWindow, fetchHistory]);

  // Auth guard — super_admin only
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { state: { from: '/health' } });
    } else if (user.role !== 'super_admin') {
      // Segment admins go back to their own dashboard, not the end-user page
      navigate(user.role === 'admin' ? '/admin' : '/');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user || user.role !== 'super_admin') return null;

  const getStatusColor = (percent) => {
    if (percent < 60) return 'status-good';
    if (percent < 80) return 'status-warning';
    return 'status-critical';
  };

  const formatBytes = (mb) => {
    if (mb < 1024) return `${mb} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  if (loading) {
    return (
      <div className="health-dashboard">
        <div className="health-loading">Loading metrics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="health-dashboard">
        <div className="health-error">
          <h2>⚠️ Error Loading Metrics</h2>
          <p>{error}</p>
          <button onClick={fetchMetrics}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="health-dashboard">
      <div className="health-header">
        <div>
          <h1>System Health Dashboard</h1>
          <p className="health-subtitle">Real-time monitoring and resource utilization</p>
        </div>
        <div className="health-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>
          <button onClick={fetchMetrics} className="refresh-btn">
            🔄 Refresh Now
          </button>
          <UserMenu />
        </div>
      </div>

      {/* Status Cards */}
      <div className="status-grid">
        <div className="status-card">
          <div className="status-icon">✅</div>
          <div className="status-label">System Status</div>
          <div className="status-value">{metrics.status?.toUpperCase() ?? '—'}</div>
        </div>

        <div className="status-card">
          <div className="status-icon">💾</div>
          <div className="status-label">Database</div>
          <div className="status-value">{metrics.database}</div>
        </div>

        <div className="status-card">
          <div className="status-icon">⏱️</div>
          <div className="status-label">Uptime</div>
          <div className="status-value">{metrics.metrics?.uptime?.formatted ?? '—'}</div>
        </div>

        <div className="status-card">
          <div className="status-icon">👥</div>
          <div className="status-label">Active Users
            <select
              value={activeWindow}
              onChange={(e) => setActiveWindow(Number(e.target.value))}
              style={{ marginLeft: 6, fontSize: '0.75rem', padding: '1px 4px', borderRadius: 4, cursor: 'pointer' }}
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={60}>1m</option>
              <option value={300}>5m</option>
            </select>
          </div>
          <div className="status-value">{metrics.metrics?.activeUsers ?? '—'}</div>
        </div>


      </div>

      {/* Metrics Grid */}
      <div className="metrics-grid">
        {/* Memory Usage */}
        <div className="metric-card">
          <h3>Memory Usage</h3>
          <div className="metric-chart">
            <div className="progress-ring">
              <svg viewBox="0 0 120 120">
                <circle
                  className="progress-ring-bg"
                  cx="60"
                  cy="60"
                  r="50"
                  strokeWidth="10"
                  fill="none"
                />
                <circle
                  className={`progress-ring-fill ${getStatusColor(metrics.metrics?.memory?.usagePercent ?? 0)}`}
                  cx="60"
                  cy="60"
                  r="50"
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${(metrics.metrics?.memory?.usagePercent ?? 0) * 3.14} 314`}
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="60" textAnchor="middle" dy="0.3em" className="progress-text">
                  {metrics.metrics?.memory?.usagePercent ?? 0}%
                </text>
              </svg>
            </div>
          </div>
          <div className="metric-details">
            <div className="metric-row">
              <span>Used:</span>
              <strong>{formatBytes(metrics.metrics?.memory?.used ?? 0)}</strong>
            </div>
            <div className="metric-row">
              <span>Total:</span>
              <strong>{formatBytes(metrics.metrics?.memory?.total ?? 0)}</strong>
            </div>
            <div className="metric-row">
              <span>Heap:</span>
              <strong>{formatBytes(metrics.metrics?.memory?.heapUsed ?? 0)} / {formatBytes(metrics.metrics?.memory?.heapTotal ?? 0)}</strong>
            </div>
            <div className="metric-row">
              <span>RSS:</span>
              <strong>{formatBytes(metrics.metrics?.memory?.rss ?? 0)}</strong>
            </div>
          </div>
        </div>

        {/* CPU Usage */}
        <div className="metric-card">
          <h3>CPU Usage</h3>
          <div className="metric-chart">
            <div className="progress-ring">
              <svg viewBox="0 0 120 120">
                <circle
                  className="progress-ring-bg"
                  cx="60"
                  cy="60"
                  r="50"
                  strokeWidth="10"
                  fill="none"
                />
                <circle
                  className={`progress-ring-fill ${getStatusColor(metrics.metrics?.cpu?.usagePercent ?? 0)}`}
                  cx="60"
                  cy="60"
                  r="50"
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${Math.min(metrics.metrics?.cpu?.usagePercent ?? 0, 100) * 3.14} 314`}
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="60" textAnchor="middle" dy="0.3em" className="progress-text">
                  {(metrics.metrics?.cpu?.usagePercent ?? 0).toFixed(1)}%
                </text>
              </svg>
            </div>
          </div>
          <div className="metric-details">
            <div className="metric-row">
              <span>User Time:</span>
              <strong>{((metrics.metrics?.cpu?.user ?? 0) / 1000000).toFixed(2)}s</strong>
            </div>
            <div className="metric-row">
              <span>System Time:</span>
              <strong>{((metrics.metrics?.cpu?.system ?? 0) / 1000000).toFixed(2)}s</strong>
            </div>
            <div className="metric-row">
              <span>Platform:</span>
              <strong>{metrics.metrics?.process?.platform ?? '—'}</strong>
            </div>
            <div className="metric-row">
              <span>Node Version:</span>
              <strong>{metrics.metrics?.process?.version ?? '—'}</strong>
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="metric-card info-card">
          <h3>System Information</h3>
          <div className="info-list">
            <div className="info-item">
              <div className="info-icon">🆔</div>
              <div>
                <div className="info-label">Process ID</div>
                <div className="info-value">{metrics.metrics?.process?.pid ?? '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">⏰</div>
              <div>
                <div className="info-label">Uptime</div>
                <div className="info-value">{metrics.metrics?.uptime?.formatted ?? '—'}</div>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">🕐</div>
              <div>
                <div className="info-label">Last Check</div>
                <div className="info-value">{new Date(metrics.timestamp).toLocaleTimeString()}</div>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">🖥️</div>
              <div>
                <div className="info-label">Platform</div>
                <div className="info-value">{metrics.metrics?.process?.platform ?? '—'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Indicators */}
      <div className="health-indicators">
        <h3>Health Status Indicators</h3>
        <div className="indicator-grid">
          <div className={`indicator ${(metrics.metrics?.memory?.usagePercent ?? 0) < 80 ? 'indicator-good' : 'indicator-critical'}`}>
            <div className="indicator-dot"></div>
            <span>Memory: {(metrics.metrics?.memory?.usagePercent ?? 0) < 80 ? 'Healthy' : 'High Usage'}</span>
          </div>
          <div className={`indicator ${(metrics.metrics?.cpu?.usagePercent ?? 0) < 80 ? 'indicator-good' : 'indicator-critical'}`}>
            <div className="indicator-dot"></div>
            <span>CPU: {(metrics.metrics?.cpu?.usagePercent ?? 0) < 80 ? 'Healthy' : 'High Usage'}</span>
          </div>
          <div className={`indicator ${metrics.database === 'connected' ? 'indicator-good' : 'indicator-critical'}`}>
            <div className="indicator-dot"></div>
            <span>Database: {metrics.database === 'connected' ? 'Connected' : 'Disconnected'}</span>
          </div>
          <div className={`indicator ${(metrics.metrics.activeUsers ?? 0) < 100 ? 'indicator-good' : 'indicator-warning'}`}>
            <div className="indicator-dot"></div>
            <span>Users: {(metrics.metrics.activeUsers ?? 0) < 100 ? 'Normal' : 'High Traffic'}</span>
          </div>
        </div>
      </div>

      {/* Users Over Time Graph */}
      {(() => {
        const history = historyData;

        const handleDownloadExcel = async () => {
          if (history.length === 0) return;
          const rows = history.map(p => {
            const d = new Date(p.ts);
            return {
              date: d.toLocaleDateString(),
              time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              count: p.count,
            };
          });
          await writeXlsxFile(rows, {
            schema: [
              { column: 'Date',       type: String, value: r => r.date },
              { column: 'Time',       type: String, value: r => r.time },
              { column: 'User Count', type: Number, value: r => r.count },
            ],
            fileName: `users-over-time_${dateStart}_${dateEnd}.xlsx`,
            sheet: 'Users Over Time',
          });
        };

        const header = (
          <div className="chart-header">
            <h3>Users Over Time</h3>
            <div className="chart-controls">
              <label className="chart-date-label">
                From
                <input
                  type="date"
                  value={dateStart}
                  onChange={(e) => { maxCountEver.current = 1; setHistoryData([]); setDateStart(e.target.value); }}
                  className="chart-date-input"
                />
              </label>
              <label className="chart-date-label">
                To
                <input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => { maxCountEver.current = 1; setHistoryData([]); setDateEnd(e.target.value); }}
                  className="chart-date-input"
                />
              </label>
              <button onClick={handleDownloadExcel} className="chart-download-btn" disabled={history.length === 0}>
                📥 Download Excel
              </button>
            </div>
          </div>
        );

        if (history.length === 0) {
          return (
            <div className="user-history-chart">
              {header}
              <p className="chart-no-data">No data for the selected date range.</p>
            </div>
          );
        }

        // Summary stats
        const counts = history.map(p => p.count);
        const avgCount = (counts.reduce((a, b) => a + b, 0) / counts.length).toFixed(1);
        const peakCount = Math.max(...counts);

        const W = 600, H = 160, PAD = { top: 16, right: 16, bottom: 32, left: 36 };
        const innerW = W - PAD.left - PAD.right;
        const innerH = H - PAD.top - PAD.bottom;
        const currentMax = Math.max(...counts, 1);
        maxCountEver.current = Math.max(maxCountEver.current, currentMax);
        const maxCount = maxCountEver.current;

        const minTs = history[0].ts;
        const maxTs = history[history.length - 1].ts;

        const xScale = ts => PAD.left + ((ts - minTs) / (maxTs - minTs || 1)) * innerW;
        const yScale = count => PAD.top + innerH - (count / maxCount) * innerH;
        const points = history.map(p => `${xScale(p.ts)},${yScale(p.count)}`).join(' ');
        const areaPoints = [
          `${PAD.left},${PAD.top + innerH}`,
          ...history.map(p => `${xScale(p.ts)},${yScale(p.count)}`),
          `${xScale(history[history.length - 1].ts)},${PAD.top + innerH}`,
        ].join(' ');
        const yLabels = [0, Math.ceil(maxCount / 2), maxCount];
        const fmtTime = ts => {
          const d = new Date(ts);
          if (dateStart === dateEnd) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        };

        const handleMouseMove = (e) => {
          const svg = chartRef.current;
          if (!svg) return;
          const rect = svg.getBoundingClientRect();
          const svgX = (e.clientX - rect.left) * (W / rect.width);
          let closest = null, minDist = Infinity;
          history.forEach(p => {
            const dist = Math.abs(xScale(p.ts) - svgX);
            if (dist < minDist) { minDist = dist; closest = p; }
          });
          if (closest) {
            setHoveredPoint({ x: xScale(closest.ts), y: yScale(closest.count), count: closest.count, ts: closest.ts });
          }
        };

        const tooltipW = 72, tooltipH = 22;

        return (
          <div className="user-history-chart">
            {header}
            <svg
              ref={chartRef}
              viewBox={`0 0 ${W} ${H}`}
              className="chart-svg"
              preserveAspectRatio="xMidYMid meet"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              {yLabels.map(v => (
                <line key={v} x1={PAD.left} y1={yScale(v)} x2={PAD.left + innerW} y2={yScale(v)} className="chart-gridline" />
              ))}
              {yLabels.map(v => (
                <text key={v} x={PAD.left - 6} y={yScale(v) + 4} className="chart-axis-label" textAnchor="end">{v}</text>
              ))}
              <text x={PAD.left} y={H - 4} className="chart-axis-label" textAnchor="start">{fmtTime(minTs)}</text>
              <text x={PAD.left + innerW} y={H - 4} className="chart-axis-label" textAnchor="end">{fmtTime(maxTs)}</text>
              <polygon points={areaPoints} className="chart-area" />
              <polyline points={points} className="chart-line" />
              {hoveredPoint && (() => {
                const tx = Math.min(Math.max(hoveredPoint.x - tooltipW / 2, PAD.left), PAD.left + innerW - tooltipW);
                const ty = hoveredPoint.y - tooltipH - 8 < PAD.top ? hoveredPoint.y + 8 : hoveredPoint.y - tooltipH - 8;
                return (
                  <g className="chart-tooltip-group">
                    <line x1={hoveredPoint.x} y1={PAD.top} x2={hoveredPoint.x} y2={PAD.top + innerH} className="chart-hover-line" />
                    <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={4} className="chart-hover-dot" />
                    <rect x={tx} y={ty} width={tooltipW} height={tooltipH} rx={4} className="chart-tooltip-bg" />
                    <text x={tx + tooltipW / 2} y={ty + 15} className="chart-tooltip-label" textAnchor="middle">{hoveredPoint.count} users</text>
                  </g>
                );
              })()}
            </svg>
            <div className="chart-summary-stats">
              <div className="chart-stat">
                <span className="chart-stat-label">Average Users</span>
                <span className="chart-stat-value">{avgCount}</span>
              </div>
              <div className="chart-stat">
                <span className="chart-stat-label">Peak Users</span>
                <span className="chart-stat-value">{peakCount}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Recommendations */}
      {((metrics.metrics?.memory?.usagePercent ?? 0) > 80 || (metrics.metrics?.cpu?.usagePercent ?? 0) > 80) && (
        <div className="recommendations">
          <h3>⚠️ Recommendations</h3>
          <ul>
            {(metrics.metrics?.memory?.usagePercent ?? 0) > 80 && (
              <li>Memory usage is high ({metrics.metrics?.memory?.usagePercent}%). Consider scaling up backend instances.</li>
            )}
            {(metrics.metrics?.cpu?.usagePercent ?? 0) > 80 && (
              <li>CPU usage is high ({(metrics.metrics?.cpu?.usagePercent ?? 0).toFixed(1)}%). Scale up for better performance.</li>
            )}
            <li>
              Run scaling command: <code>.\infrastructure\scaling\scale-up.ps1 -Replicas 5</code>
            </li>
          </ul>
        </div>
      )}

      <div className="health-footer">
        <a href="/super-admin" className="back-link">← Back to Dashboard</a>
        <span className="health-timestamp">
          Last updated: {new Date(metrics.timestamp).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default HealthDashboard;
